import { ColDef, ColGroupDef, GridOptions } from 'ag-grid-community'
import moment from 'moment'
import { CollectionType, NegotiationDetail, NegotiationStatus, NegotiationType, SummaryRowDirection } from 'api'
import { DecisionMakerHelper, getCollateralMarket, getDateEditorFormat, MarketHelper, UserHelper } from 'model'
import { NumericInput, TextInput } from 'view/components/inputs'
import {
  AssignableNameCellRenderer,
  axeTradeConditionKeyCreator,
  createActionsRenderer,
  createAxeTradeConditionRenderer,
  createGroupedActionRenderer,
  createMasterDetailRowColDef,
  dateFilterParams,
  gridOptionsBase,
  StandardGridColumnId,
  StatusCellRenderer,
  tradesGridGroupingRowColDef,
  TypedCellRendererParams,
  TypedContext,
  TypedRowNode,
  TypedValueGetterParams,
  ValidationCellRenderer,
  withAllContext
} from 'view/components/datagrid'
import {
  axeFilterValueFormatter,
  axeTradeConditionFilterTextGetter,
  booleanFormatter,
  callableTypeFormatter,
  canEditIncomingNegotiationDetailEndDate,
  canEditIncomingNegotiationDetailStartConsiderationOverride,
  canEditNegotiationDetailBondPrice,
  canEditNegotiationDetailCounterpartyId,
  canEditNegotiationDetailCounterpartySponsor,
  canEditNegotiationDetailFee,
  canEditNegotiationDetailGeneral,
  canEditNegotiationDetailHaircut,
  canEditNegotiationDetailHaircutType,
  canEditNegotiationDetailNotional,
  canEditNegotiationDetailOtw,
  canEditNegotiationDetailRate,
  canEditNegotiationDetailRFQType,
  canEditNegotiationDetailRowSettlementType,
  canEditNegotiationDetailStartConsideration,
  canEditNegotiationPreAgreed,
  collectionFilterFormatter,
  collectionFormatter,
  collectionKeyCreator,
  dateQuickFilterTextGetter,
  evergreenNoticeFormatter,
  evergreenNoticePeriodQuickfixFilterTextGetter,
  getFeeFormatOptions,
  getHaircutFormatOptions,
  getNegotiationClearingStatusForNonSummary,
  getNegotiationSummary,
  getNotionalFormatOptions,
  getOwnDirection,
  getRateFormatOptions,
  getSettlementVenueName,
  getStartConsiderationFormatOptions,
  getTargetValueIfNotOcoSummary,
  getTargetValueIfNotSummary,
  haircutTypeFormatter,
  hasNegotiationNestedLegs,
  isMultiDirectionSummary,
  makeCusipGetter,
  makeDateFormatter,
  makeExecutingTradingEntityNameGetter,
  makeFeeFormatter,
  makeHaircutFilterGetter,
  makeHaircutFormatter,
  makeHaircutQuickFilterTextGetter,
  makeIsinGetter,
  makeNotionalFilterGetter,
  makeNotionalFormatter,
  makeNumberFilterGetter,
  makeNumberFormatter,
  makeNumberFormatterSummaryRow,
  makeNumberQuickFilterTextGetter,
  makeOrganisationFieldGetter,
  makePrevRateFilterGetter,
  makeRateFilterGetter,
  makeRateFormatter,
  makeRateQuickFilterTextGetter,
  makeSponsorNameGetter,
  makeStartConsiderationFilterGetter,
  makeStartConsiderationFormatter,
  makeTimeFormatter,
  makeTradingEntityFieldGetter,
  makeTradingEntityLeiGetter,
  makeTradingEntityNameGetter,
  negotiationRowDataGetter,
  notionalHeaderValueGetter,
  orderTermFormatter,
  orderTermQuickFilterTextGetter,
  preTradeOrTradingEntityNameGetter,
  repoClassificationFormatter,
  rfqTypeFormatter,
  timeQuickFilterTextGetter
} from 'view/shared/datagrid'
import {
  BooleanSelectbox,
  CounterpartySponsorCombobox,
  DecisionMakerSelectbox,
  FilteredTermSelectbox,
  OTWSelectbox,
  RFQTypeSelectbox,
  SettlementVenueIncomingSettlementSelectbox,
  TradingDatePicker
} from 'view/shared/entity'
import { getDynamicDetailRowHeight } from 'view/shared/preAssignments'
import {
  getBondPriceModificationType,
  getBooleanModificationType,
  getEndDateDelta,
  getEndDateModificationType,
  getHaircutDelta,
  getHaircutModificationType,
  getHaircutTypeModificationType,
  getNotionalDelta,
  getNotionalModificationType,
  getRateModificationType,
  getRFQTypeModificationType,
  getStartConsiderationDelta,
  getStartConsiderationModificationType,
  getStartConsiderationOverrideModificationType,
  getStartDateDelta,
  getStartDateModificationType,
  getStringModificationType,
  getTermModificationType
} from 'view/routes/requests/shared/deltaGetters'
import {
  getLiveStatusColor,
  getNegotiationStatusAsString,
  isSummaryNegotiationType,
  negotiationIsNewPriced
} from 'view/routes/requests/shared/utils'
import { LiveGridColumnId, NegotiationFieldNames } from '../LiveNegotiationsGrid/LiveGridColumnNames'
import { ClearingGridColumnId, CollateralBasketColumnId, CollectionColumnId } from 'view/routes/ColumnNames'
import { DeltaGroupHeaderComponent } from '../LiveNegotiationsGrid/DeltaGroupHeaderComponent'
import { headerTemplateDelta } from '../LiveNegotiationsGrid/headerTemplates'
import { LiveActionsCell } from '../LiveNegotiationsGrid/LiveActionsCell'
import { TimeRenderer } from '../LiveNegotiationsGrid/TimeRenderer'
import { NegotiableCellRenderer } from '../LiveNegotiationsGrid/NegotiableCellRenderer'
import { LiveAxeTradeConditionButtons } from '../LiveNegotiationsGrid/LiveAxeTradeConditionButtons'
import { getNegotiatedValue } from '../shared/utils'
import { getTimeLeft, stopPastePropagation } from 'utils'
import { incomingNegotiationDetailsToTopLevelDetailsTransformerSelector } from 'selectors/negotiations'
import { Locale, Translation } from 'locale'
import * as constants from 'view/styles/constants'
import { HaircutTypeSelectbox } from 'view/shared/entity/haircutOptions/HaircutTypeSelectbox'
import { FilteredCheckboxHeaderRenderer } from 'view/components/datagrid/renderers/FilteredCheckboxHeaderRenderer'
import { swallowPrecisionError } from 'utils/Decimal'
import { isNotNil } from 'utils/predicate'
import {
  CounterpartyMultiLegCombobox,
  CounterpartyMultiLegComboboxProps
} from 'view/shared/entity/tradingEntity/CounterpartyMultiLegCombobox'
import { IncomingPreAssignmentsSubGrid } from './IncomingPreAssignmentsSubGrid'
import { StagingGridColumnId } from 'view/routes/staging/StagingOrdersGrid/StagingGridColumnNames'
import {
  BASE_ASSET_TYPE,
  BASE_COLLECTION_TYPE,
  BASE_DIRECTION,
  BASE_ISSUE,
  BASE_MARKET_TYPE,
  BASE_MIC,
  BASE_REPO_TYPE,
  BASE_ROE,
  BASE_ROS
} from 'view/shared/datagrid/baseColumns'
import { CounterpartyFilter } from 'selectors'

const ALL_POSSIBLE_STATUSES = [
  Translation.TC_ALL,
  Translation.TC_NEW,
  Translation.TC_ROLL,
  Translation.TC_UNWIND,
  Translation.TC_SUB,
  Translation.TC_READY_TO_SEND,
  Translation.TC_COUNTER_IN,
  Translation.TC_REFRESHED,
  Translation.TC_QUOTED,
  Translation.TC_QUOTE_SUBJECT,
  Translation.TC_WITHDRAWN,
  Translation.TC_NONE
].map(translation => Locale.translate(translation))

const VISIBLE_COLUMNS = [
  /* ====================================================== Visible by default ====================================================== */
  {
    colId: StandardGridColumnId.CHECKBOX,
    headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
    headerComponent: FilteredCheckboxHeaderRenderer,
    headerComponentParams: {
      allPossibleStatuses: ALL_POSSIBLE_STATUSES,
      getStatusAsString: getNegotiationStatusAsString,
      withPortal: true
    },
    checkboxSelection: true,
    width: 75,
    maxWidth: 75,
    minWidth: 75,
    pinned: 'left' as const,
    lockPinned: true,
    enableRowGroup: false,
    lockPosition: true,
    lockVisible: true,
    filter: false,
    resizable: false,
    sortable: false
  },
  tradesGridGroupingRowColDef,
  {
    colId: StandardGridColumnId.ACTIONS,
    headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
    equals: () => false,
    width: 240,
    minWidth: 240,
    headerComponent: withAllContext(
      createGroupedActionRenderer(LiveActionsCell, incomingNegotiationDetailsToTopLevelDetailsTransformerSelector)
    ),
    cellClass: 'action-cell',
    cellRenderer: withAllContext(
      createActionsRenderer(LiveActionsCell, negotiationRowDataGetter, incomingNegotiationDetailsToTopLevelDetailsTransformerSelector)
    ),
    pinned: 'left' as const,
    lockPinned: true,
    enableRowGroup: false,
    lockPosition: true,
    lockVisible: true,
    filter: false,
    sortable: false
  },
  createMasterDetailRowColDef({
    isRowMaster: hasNegotiationNestedLegs
  }),
  {
    colId: LiveGridColumnId.NEGOTIATION_STATUS,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
      const rowData = negotiationRowDataGetter(params)

      if (!params.node.group && rowData?.negotiation?.summaryNegotiationId && rowData?.collection?.type === CollectionType.ALL_OR_NOTHING) {
        const result: NegotiationDetail | null = getNegotiationSummary(rowData?.negotiation?.summaryNegotiationId, params)
        return result && getNegotiationStatusAsString(result)
      }

      return rowData && getNegotiationStatusAsString(rowData)
    },
    headerName: Locale.translate(Translation.TC_STATUS),
    width: 140,
    filter: 'agSetColumnFilter',
    cellRenderer: StatusCellRenderer,
    cellRendererParams: (params: TypedCellRendererParams<NegotiationDetail>) => {
      let rowData = negotiationRowDataGetter(params)

      if (!params.node.group && rowData?.negotiation?.summaryNegotiationId && rowData?.collection?.type === CollectionType.ALL_OR_NOTHING) {
        rowData = getNegotiationSummary(rowData?.negotiation?.summaryNegotiationId, params)
      }

      return {
        rowDataGetter: negotiationRowDataGetter,
        getStatusColor: () => rowData && (negotiationIsNewPriced(rowData) ? constants.STATUS_COLOR_ACTIVE : getLiveStatusColor(rowData))
      }
    },
    equals: () => false,
    lockVisible: true
  },
  {
    colId: LiveGridColumnId.COUNTERPARTY_NAME,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
      const rowData = negotiationRowDataGetter(params)
      return rowData && preTradeOrTradingEntityNameGetter(false, rowData)
    },
    headerName: Locale.translate(Translation.TC_COUNTERPARTY),
    width: 150,
    filter: 'agSetColumnFilter',
    rowGroup: true
  },
  {
    colId: LiveGridColumnId.OTW_DURATION,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
      const rowData = negotiationRowDataGetter(params)
      return rowData?.negotiation.status === NegotiationStatus.QUOTED ? rowData.negotiation.otwEndTime : rowData?.otwDuration
    },
    headerName: Locale.translate(Translation.TC_OTW_TIME),
    equals: () => false,
    width: 84,
    minWidth: 84,
    cellRenderer: withAllContext(TimeRenderer),
    cellRendererParams: {
      which: 'otw'
    },
    cellEditorParams: {
      getNodeId: (data: NegotiationDetail) => data.negotiation.negotiationId,
      inputComponent: OTWSelectbox,
      inputParams: (negotiationDetail: NegotiationDetail) => ({
        remainingRFQTimeMins: moment.duration(getTimeLeft(negotiationDetail.negotiation.rfqEndTime)).asMinutes(),
        dataTestId: 'IncomingNegotiationsGridModel-OTWSelectbox',
        onPaste: stopPastePropagation,
        withPortal: true
      }),
      updateField: NegotiationFieldNames.OTW_DURATION,
      rowDataGetter: negotiationRowDataGetter
    },
    editable: canEditNegotiationDetailOtw,
    lockPinned: true,
    enableRowGroup: false
  },
  {
    colId: LiveGridColumnId.RFQ_TIMER,
    // TODO
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.negotiation.rfqEndTime,
    headerName: Locale.translate(Translation.TC_RFQ_TIME),
    equals: () => false,
    width: 84,
    minWidth: 84,
    cellRenderer: withAllContext(TimeRenderer),
    cellRendererParams: {
      which: 'rfq'
    },
    enableRowGroup: false
  },
  {
    colId: CollectionColumnId.COLLECTION_NAME,
    headerName: Locale.translate(Translation.TC_COLLECTION_NAME),
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.collection,
    keyCreator: collectionKeyCreator,
    valueFormatter: collectionFormatter,
    cellRenderer: AssignableNameCellRenderer,
    cellRendererParams: {
      unassignedLabel: Locale.translate(Translation.TC_COLLECTION_UNASSIGNED)
    },
    width: 150,
    filter: 'agSetColumnFilter',
    filterParams: {
      valueFormatter: collectionFilterFormatter
    },
    hide: true
  },
  {
    ...BASE_COLLECTION_TYPE,
    hide: true
  },
  {
    colId: LiveGridColumnId.NEGOTIATION_ID,
    headerName: Locale.translate(Translation.TC_NEGOTIATION_ID),
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.negotiation.negotiationId,
    width: 120,
    filter: 'agSetColumnFilter',
    hide: true
  },
  {
    ...BASE_DIRECTION,
    colId: LiveGridColumnId.NEGOTIATION_DIRECTION,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
      const rowData = negotiationRowDataGetter(params)
      if (!rowData) {
        return null
      }
      if (isMultiDirectionSummary(rowData)) {
        return SummaryRowDirection.MULTIPLE
      }
      return rowData && getOwnDirection(rowData.negotiation.direction, rowData.ownOrder)
    },
    headerName: Locale.translate(Translation.TC_ABBREVIATED_DIRECTION),
    cellRendererParams: {
      rowDataGetter: negotiationRowDataGetter
    }
  },
  {
    colId: LiveGridColumnId.COLLATERAL_NAME,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
      const rowData = negotiationRowDataGetter(params)
      return rowData?.collateral?.name ?? rowData?.collateralBasket?.name
    },
    headerName: Locale.translate(Translation.TC_COLLATERAL),
    width: 120,
    filter: 'agSetColumnFilter'
  },
  {
    colId: LiveGridColumnId.SECONDARY_COLLATERAL_NAME,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
      const rowData = negotiationRowDataGetter(params)
      return rowData?.secondaryInstrumentName
    },
    cellRenderer: NegotiableCellRenderer,
    cellRendererParams: {
      getModificationType: ({ negotiation }: NegotiationDetail) => getStringModificationType(negotiation.secondaryBasketId),
      validationField: NegotiationFieldNames.NOTIONAL,
      rowDataGetter: negotiationRowDataGetter
    },
    headerName: Locale.translate(Translation.TC_SECONDARY_COLLATERAL),
    width: 120,
    filter: 'agSetColumnFilter'
  },
  {
    colId: LiveGridColumnId.NEGOTIATION_STOCK_BORROWING_LENDING_TYPE,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
      const rowData = negotiationRowDataGetter(params)
      return (
        rowData &&
        rowData.negotiation.stockBorrowingLendingType &&
        getTargetValueIfNotOcoSummary(rowData, getNegotiatedValue(rowData.negotiation.stockBorrowingLendingType))
      )
    },
    headerName: Locale.translate(Translation.TC_STOCK_BORROW_LENDING_TYPE),
    width: 120,
    filter: 'agSetColumnFilter'
  },
  {
    marryChildren: true,
    children: [
      {
        colId: LiveGridColumnId.NEGOTIATION_NOTIONAL,
        headerValueGetter: notionalHeaderValueGetter,
        headerName: Locale.translate(Translation.TC_NOTIONAL_SHORT),
        valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
          const rowData = negotiationRowDataGetter(params)
          return rowData && getTargetValueIfNotOcoSummary(rowData, getNegotiatedValue(rowData.negotiation.notional))
        },
        equals: () => false,
        width: 110,
        type: 'numericColumn',
        filter: 'agNumberColumnFilter',
        headerClass: 'group-header-cell',
        headerComponent: DeltaGroupHeaderComponent,
        headerComponentParams: {
          children: [LiveGridColumnId.NEGOTIATION_NOTIONAL_ORIGINAL, LiveGridColumnId.NOTIONAL_DELTA]
        },
        valueFormatter: makeNotionalFormatter(false, negotiationRowDataGetter),
        filterValueGetter: makeNotionalFilterGetter(),
        cellRenderer: NegotiableCellRenderer,
        cellRendererParams: {
          getModificationType: ({ negotiation }: NegotiationDetail) => getNotionalModificationType(negotiation.notional),
          validationField: NegotiationFieldNames.NOTIONAL,
          rowDataGetter: negotiationRowDataGetter
        },
        editable: canEditNegotiationDetailNotional,
        cellEditorParams: {
          inputComponent: NumericInput,
          inputParams: (negotiationDetail: NegotiationDetail, context: any) => ({
            min: negotiationDetail.collateral?.ticketIncrement,
            step: negotiationDetail.collateral?.ticketIncrement,
            formatOptions: getNotionalFormatOptions(context.userPreference.amountFormat),
            onPaste: stopPastePropagation
          }),
          defaultField: NegotiationFieldNames.NOTIONAL_NEGOTIATED,
          updateField: NegotiationFieldNames.NOTIONAL_MODIFIED,
          isModifiedField: NegotiationFieldNames.NOTIONAL_IS_MODIFIED,
          validationField: NegotiationFieldNames.NOTIONAL
        },
        lockPinned: true,
        enableRowGroup: false,
        cellClassRules: {
          'paren-on-negative-cell': (params: any) => params.node.group && params.value > 0
        }
      },
      {
        colId: LiveGridColumnId.NEGOTIATION_NOTIONAL_ORIGINAL,
        valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
          const rowData = negotiationRowDataGetter(params)
          return getTargetValueIfNotSummary(rowData, rowData?.negotiation.notional.original)
        },
        headerValueGetter: (params: TypedValueGetterParams<NegotiationDetail>) =>
          Locale.translate(Translation.TC_ORIGINAL, { amountFormat: params.context.userPreference.amountFormat }),
        width: 115,
        type: 'numericColumn',
        filter: 'agNumberColumnFilter',
        headerClass: 'group-header-cell group-header-cell-right-align',
        headerComponentParams: {
          parent: LiveGridColumnId.NEGOTIATION_NOTIONAL
        },
        valueFormatter: makeNotionalFormatter(),
        filterValueGetter: makeNotionalFilterGetter(),
        enableRowGroup: false,
        hide: true // delta groups are initially hidden
      },
      {
        colId: LiveGridColumnId.NOTIONAL_DELTA,
        valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
          const rowData = negotiationRowDataGetter(params)
          return getTargetValueIfNotOcoSummary(rowData, getNotionalDelta(rowData?.negotiation.notional))
        },
        headerValueGetter: notionalHeaderValueGetter,
        width: 110,
        type: 'numericColumn',
        filter: 'agNumberColumnFilter',
        headerClass: 'group-header-cell group-header-cell-right-align',
        headerComponentParams: {
          template: headerTemplateDelta,
          parent: LiveGridColumnId.NEGOTIATION_NOTIONAL,
          delta: true
        },
        valueFormatter: makeNotionalFormatter(true),
        filterValueGetter: makeNotionalFilterGetter(true),
        getQuickFilterText: makeNumberQuickFilterTextGetter({ sign: true }),
        enableRowGroup: false,
        hide: true // delta groups are initially hidden
      }
    ]
  },
  {
    colId: LiveGridColumnId.NEGOTIATION_TERM,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
      const rowData = negotiationRowDataGetter(params)
      return rowData && getTargetValueIfNotOcoSummary(rowData, getNegotiatedValue(rowData.negotiation.term))
    },
    headerName: Locale.translate(Translation.TC_TERM),
    equals: () => false,
    width: 120,
    filter: 'agSetColumnFilter',
    cellRenderer: NegotiableCellRenderer,
    cellRendererParams: {
      getModificationType: ({ negotiation }: NegotiationDetail) => getTermModificationType(negotiation.term),
      validationField: NegotiationFieldNames.TERM,
      rowDataGetter: negotiationRowDataGetter
    },
    cellEditorParams: {
      inputComponent: FilteredTermSelectbox,
      inputParams: (negotiationDetail: NegotiationDetail) => ({
        repoClassification: negotiationDetail.negotiation.repoClassification,
        onPaste: stopPastePropagation,
        withPortal: true
      }),
      defaultField: NegotiationFieldNames.TERM_NEGOTIATED,
      updateField: NegotiationFieldNames.TERM_MODIFIED,
      isModifiedField: NegotiationFieldNames.TERM_IS_MODIFIED,
      validationField: NegotiationFieldNames.TERM
    },
    valueFormatter: orderTermFormatter,
    getQuickFilterText: orderTermQuickFilterTextGetter,
    editable: canEditNegotiationDetailGeneral,
    lockPinned: true,
    enableRowGroup: true
  },
  {
    marryChildren: true,
    children: [
      {
        colId: LiveGridColumnId.NEGOTIATION_START_DATE,
        valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
          const rowData = negotiationRowDataGetter(params)
          return rowData && getTargetValueIfNotOcoSummary(rowData, getNegotiatedValue(rowData.negotiation.startDate))
        },
        headerName: Locale.translate(Translation.TC_START_DATE),
        equals: () => false,
        width: 120,
        filter: 'agDateColumnFilter',
        ...dateFilterParams,
        headerClass: 'group-header-cell',
        headerComponent: DeltaGroupHeaderComponent,
        headerComponentParams: {
          children: [LiveGridColumnId.NEGOTIATION_START_DATE_TARGET, LiveGridColumnId.START_DATE_DELTA]
        },
        valueFormatter: makeDateFormatter(),
        getQuickFilterText: dateQuickFilterTextGetter,
        cellRenderer: NegotiableCellRenderer,
        cellRendererParams: {
          getModificationType: ({ negotiation }: NegotiationDetail) => getStartDateModificationType(negotiation.startDate),
          validationField: NegotiationFieldNames.START_DATE,
          rowDataGetter: negotiationRowDataGetter
        },
        cellEditorParams: {
          inputComponent: TradingDatePicker,
          inputParams: (negotiationDetail: NegotiationDetail, context: any) => ({
            currency: negotiationDetail.negotiation.currency,
            market: negotiationDetail.collateral && getCollateralMarket(negotiationDetail.collateral),
            dateFormat: getDateEditorFormat(context.userPreference.dateFormat),
            onPaste: stopPastePropagation,
            withPortal: true
          }),
          defaultField: NegotiationFieldNames.START_DATE_NEGOTIATED,
          updateField: NegotiationFieldNames.START_DATE_MODIFIED,
          isModifiedField: NegotiationFieldNames.START_DATE_IS_MODIFIED,
          validationField: NegotiationFieldNames.START_DATE
        },
        editable: canEditNegotiationDetailGeneral,
        lockPinned: true,
        enableRowGroup: false
      },
      {
        colId: LiveGridColumnId.NEGOTIATION_START_DATE_TARGET,
        valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
          const rowData = negotiationRowDataGetter(params)
          return getTargetValueIfNotSummary(rowData, rowData?.negotiation.startDate.original)
        },
        headerName: Locale.translate(Translation.TC_TARGET),
        width: 120,
        filter: 'agDateColumnFilter',
        ...dateFilterParams,
        headerClass: 'group-header-cell',
        headerComponentParams: {
          parent: LiveGridColumnId.NEGOTIATION_START_DATE
        },
        valueFormatter: makeDateFormatter(),
        getQuickFilterText: dateQuickFilterTextGetter,
        enableRowGroup: false,
        hide: true // delta groups are initially hidden
      },
      {
        colId: LiveGridColumnId.START_DATE_DELTA,
        valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
          const rowData = negotiationRowDataGetter(params)
          return getTargetValueIfNotOcoSummary(rowData, getStartDateDelta(rowData?.negotiation.startDate))
        },
        headerName: Locale.translate(Translation.TC_START_DATE),
        width: 120,
        filter: 'agDateColumnFilter',
        ...dateFilterParams,
        headerClass: 'group-header-cell',
        headerComponentParams: {
          template: headerTemplateDelta,
          parent: LiveGridColumnId.NEGOTIATION_START_DATE,
          delta: true
        },
        enableRowGroup: false,
        hide: true // delta groups are initially hidden
      }
    ]
  },
  {
    marryChildren: true,
    children: [
      {
        colId: LiveGridColumnId.NEGOTIATION_END_DATE,
        valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
          const rowData = negotiationRowDataGetter(params)
          return rowData && getTargetValueIfNotOcoSummary(rowData, getNegotiatedValue(rowData.negotiation.endDate))
        },
        headerName: Locale.translate(Translation.TC_END_DATE),
        equals: () => false,
        width: 120,
        filter: 'agDateColumnFilter',
        ...dateFilterParams,
        headerClass: 'group-header-cell',
        headerComponent: DeltaGroupHeaderComponent,
        headerComponentParams: {
          children: [LiveGridColumnId.NEGOTIATION_END_DATE_TARGET, LiveGridColumnId.END_DATE_DELTA]
        },
        valueFormatter: makeDateFormatter(),
        getQuickFilterText: dateQuickFilterTextGetter,
        cellRenderer: NegotiableCellRenderer,
        cellRendererParams: {
          getModificationType: ({ negotiation }: NegotiationDetail) => getEndDateModificationType(negotiation.endDate),
          validationField: NegotiationFieldNames.END_DATE,
          rowDataGetter: negotiationRowDataGetter
        },
        cellEditorParams: {
          inputComponent: TradingDatePicker,
          inputParams: (negotiationDetail: NegotiationDetail, context: any) => ({
            currency: negotiationDetail.negotiation.currency,
            dateFormat: getDateEditorFormat(context.userPreference.dateFormat),
            onPaste: stopPastePropagation,
            withPortal: true
          }),
          defaultField: NegotiationFieldNames.END_DATE_NEGOTIATED,
          updateField: NegotiationFieldNames.END_DATE_MODIFIED,
          isModifiedField: NegotiationFieldNames.END_DATE_IS_MODIFIED,
          validationField: NegotiationFieldNames.END_DATE
        },
        editable: canEditIncomingNegotiationDetailEndDate,
        lockPinned: true,
        enableRowGroup: false
      },
      {
        colId: LiveGridColumnId.NEGOTIATION_END_DATE_TARGET,
        valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
          const rowData = negotiationRowDataGetter(params)
          return getTargetValueIfNotSummary(rowData, rowData?.negotiation.endDate.original)
        },
        headerName: Locale.translate(Translation.TC_TARGET),
        width: 120,
        filter: 'agDateColumnFilter',
        ...dateFilterParams,
        headerClass: 'group-header-cell',
        headerComponentParams: {
          parent: LiveGridColumnId.NEGOTIATION_END_DATE
        },
        valueFormatter: makeDateFormatter(),
        getQuickFilterText: dateQuickFilterTextGetter,
        enableRowGroup: false,
        hide: true // delta groups are initially hidden
      },
      {
        colId: LiveGridColumnId.END_DATE_DELTA,
        valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
          const rowData = negotiationRowDataGetter(params)
          return getTargetValueIfNotOcoSummary(rowData, getEndDateDelta(rowData?.negotiation.endDate))
        },
        headerName: Locale.translate(Translation.TC_END_DATE),
        width: 120,
        filter: 'agDateColumnFilter',
        ...dateFilterParams,
        headerClass: 'group-header-cell',
        headerComponentParams: {
          template: headerTemplateDelta,
          parent: LiveGridColumnId.NEGOTIATION_END_DATE,
          delta: true
        },
        enableRowGroup: false,
        hide: true // delta groups are initially hidden
      }
    ]
  },
  {
    colId: LiveGridColumnId.MARKET_TRADE_DATE,
    headerName: Locale.translate(Translation.TC_MARKET_TRADE_DATE),
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
      return negotiationRowDataGetter<NegotiationDetail>(params)?.negotiation.marketTradeDate
    },
    width: 150,
    valueFormatter: makeDateFormatter(),
    filter: 'agSetColumnFilter'
  },
  {
    marryChildren: true,
    children: [
      {
        colId: LiveGridColumnId.NEGOTIATION_RATE,
        valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
          const rowData = negotiationRowDataGetter(params)
          return rowData && getTargetValueIfNotOcoSummary(rowData, getNegotiatedValue(rowData.negotiation.rate))
        },
        headerName: Locale.translate(Translation.TC_RATE),
        equals: () => false,
        width: 80,
        minWidth: 80,
        type: 'numericColumn',
        filter: 'agNumberColumnFilter',
        headerClass: 'group-header-cell',
        headerComponent: DeltaGroupHeaderComponent,
        headerComponentParams: {
          children: [LiveGridColumnId.NEGOTIATION_RATE_TARGET, LiveGridColumnId.NEGOTIATION_LAST_QUOTED]
        },
        cellClass: 'font-weight-bold',
        valueFormatter: makeRateFormatter(false, negotiationRowDataGetter),
        filterValueGetter: makeRateFilterGetter(),
        getQuickFilterText: makeRateQuickFilterTextGetter(),
        cellRenderer: NegotiableCellRenderer,
        cellRendererParams: {
          getModificationType: ({ negotiation }: NegotiationDetail) => getRateModificationType(negotiation.rate),
          validationField: NegotiationFieldNames.RATE,
          isRateField: true,
          rowDataGetter: negotiationRowDataGetter
        },
        editable: canEditNegotiationDetailRate,
        cellEditorParams: {
          inputComponent: NumericInput,
          inputParams: (negotiationDetail: NegotiationDetail) => ({
            formatOptions: getRateFormatOptions(negotiationDetail.referenceRate),
            updateOnBlurOnly: negotiationDetail.negotiation.negotiationType === NegotiationType.CONTINGENT_SUMMARY,
            onPaste: stopPastePropagation,
            dataTestId: 'LiveNegotiationsGridModel-RateNumericInput'
          }),
          rowDataGetter: negotiationRowDataGetter,
          getNodeId: (negotiationDetail: NegotiationDetail) => negotiationDetail.negotiation.negotiationId,
          defaultField: NegotiationFieldNames.RATE_NEGOTIATED,
          updateField: NegotiationFieldNames.RATE_MODIFIED,
          isModifiedField: NegotiationFieldNames.RATE_IS_MODIFIED,
          validationField: NegotiationFieldNames.RATE,
          useEnterForNavigation: true
        },
        lockPinned: true,
        enableRowGroup: false
      },
      {
        colId: LiveGridColumnId.NEGOTIATION_RATE_TARGET,
        valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
          const rowData = negotiationRowDataGetter(params)
          return getTargetValueIfNotSummary(rowData, rowData?.negotiation.rate.original)
        },
        headerName: Locale.translate(Translation.TC_TARGET),
        width: 80,
        minWidth: 80,
        type: 'numericColumn',
        filter: 'agNumberColumnFilter',
        headerClass: 'group-header-cell group-header-cell-right-align',
        headerComponentParams: {
          parent: LiveGridColumnId.NEGOTIATION_RATE
        },
        valueFormatter: makeRateFormatter(false, negotiationRowDataGetter),
        filterValueGetter: makeRateFilterGetter(),
        getQuickFilterText: makeRateQuickFilterTextGetter(),
        enableRowGroup: false,
        hide: true // delta groups are initially hidden
      },
      {
        colId: LiveGridColumnId.NEGOTIATION_LAST_QUOTED,
        valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
          const rowData = negotiationRowDataGetter(params)
          return getTargetValueIfNotOcoSummary(rowData, rowData?.negotiation.lastQuoted)
        },
        headerName: Locale.translate(Translation.TC_LAST_QUOTED),
        width: 100,
        minWidth: 100,
        type: 'numericColumn',
        filter: 'agNumberColumnFilter',
        headerClass: 'group-header-cell group-header-cell-right-align',
        headerComponentParams: {
          parent: LiveGridColumnId.NEGOTIATION_RATE
        },
        valueFormatter: makeRateFormatter(false, negotiationRowDataGetter),
        filterValueGetter: makeRateFilterGetter(),
        getQuickFilterText: makeRateQuickFilterTextGetter(),
        enableRowGroup: false,
        hide: true
      }
    ]
  },
  {
    colId: LiveGridColumnId.NEGOTIATION_FEE,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
      const rowData = negotiationRowDataGetter(params)
      return rowData && rowData.negotiation.fee && getTargetValueIfNotOcoSummary(rowData, getNegotiatedValue(rowData.negotiation.fee))
    },
    valueFormatter: makeFeeFormatter(),
    headerName: Locale.translate(Translation.TC_FEE),
    equals: () => false,
    width: 80,
    minWidth: 80,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    headerClass: 'group-header-cell',
    cellClass: 'font-weight-bold',
    filterValueGetter: makeRateFilterGetter(),
    getQuickFilterText: makeRateQuickFilterTextGetter(),
    cellRenderer: NegotiableCellRenderer,
    cellRendererParams: {
      getModificationType: ({ negotiation }: NegotiationDetail) => getRateModificationType(negotiation.fee),
      validationField: NegotiationFieldNames.FEE,
      isRateField: true,
      rowDataGetter: negotiationRowDataGetter
    },
    editable: canEditNegotiationDetailFee,
    cellEditorParams: {
      inputComponent: NumericInput,
      inputParams: (negotiationDetail: NegotiationDetail) => ({
        formatOptions: getFeeFormatOptions(),
        updateOnBlurOnly: negotiationDetail.negotiation.negotiationType === NegotiationType.CONTINGENT_SUMMARY,
        onPaste: stopPastePropagation,
        dataTestId: 'LiveNegotiationsGridModel-RateNumericInput'
      }),
      rowDataGetter: negotiationRowDataGetter,
      getNodeId: (negotiationDetail: NegotiationDetail) => negotiationDetail.negotiation.negotiationId,
      defaultField: NegotiationFieldNames.FEE_NEGOTIATED,
      updateField: NegotiationFieldNames.FEE_MODIFIED,
      isModifiedField: NegotiationFieldNames.FEE_IS_MODIFIED,
      validationField: NegotiationFieldNames.FEE,
      useEnterForNavigation: true
    },
    lockPinned: true,
    enableRowGroup: false
  },
  {
    colId: LiveGridColumnId.NEGOTIATION_HAIRCUT_TYPE,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
      const rowData = negotiationRowDataGetter(params)
      return rowData && getTargetValueIfNotOcoSummary(rowData, getNegotiatedValue(rowData.negotiation.haircutType))
    },
    valueFormatter: haircutTypeFormatter,
    headerName: Locale.translate(Translation.TC_COLLATERAL_HAIRCUT),
    equals: () => false,
    width: 150,
    cellRenderer: NegotiableCellRenderer,
    cellRendererParams: {
      validationField: NegotiationFieldNames.HAIRCUT_TYPE,
      rowDataGetter: negotiationRowDataGetter,
      getModificationType: ({ negotiation }: NegotiationDetail) => getHaircutTypeModificationType(negotiation.haircutType),
      isHaircutOrHaircutTypeField: true
    },
    cellEditorParams: {
      inputComponent: HaircutTypeSelectbox,
      inputParams: (_negotiationDetail: NegotiationDetail) => ({
        onPaste: stopPastePropagation,
        canClear: true,
        withPortal: true
      }),
      defaultField: NegotiationFieldNames.HAIRCUT_TYPE_NEGOTIATED,
      updateField: NegotiationFieldNames.HAIRCUT_TYPE_MODIFIED,
      isModifiedField: NegotiationFieldNames.HAIRCUT_TYPE_IS_MODIFIED,
      validationField: NegotiationFieldNames.HAIRCUT_TYPE
    },
    editable: canEditNegotiationDetailHaircutType,
    lockPinned: true,
    enableRowGroup: false
  },
  {
    marryChildren: true,
    children: [
      {
        colId: LiveGridColumnId.NEGOTIATION_HAIRCUT,
        valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
          const rowData = negotiationRowDataGetter(params)
          if (rowData && isMultiDirectionSummary(rowData)) {
            return null
          }
          return rowData && getTargetValueIfNotOcoSummary(rowData, getNegotiatedValue(rowData.negotiation.haircut))
        },
        headerName: Locale.translate(Translation.TC_HAIRCUT),
        equals: () => false,
        width: 80,
        type: 'numericColumn',
        filter: 'agNumberColumnFilter',
        headerClass: 'group-header-cell',
        headerComponent: DeltaGroupHeaderComponent,
        headerComponentParams: {
          children: [LiveGridColumnId.NEGOTIATION_HAIRCUT_TARGET, LiveGridColumnId.HAIRCUT_DELTA]
        },
        valueFormatter: makeHaircutFormatter(),
        filterValueGetter: makeHaircutFilterGetter(),
        getQuickFilterText: makeHaircutQuickFilterTextGetter(),
        cellRenderer: NegotiableCellRenderer,
        cellRendererParams: {
          getModificationType: ({ negotiation }: NegotiationDetail) => getHaircutModificationType(negotiation.haircut),
          validationField: NegotiationFieldNames.HAIRCUT,
          rowDataGetter: negotiationRowDataGetter,
          dataTestId: 'LiveNegotiationsGridModel-HaircutNegotiableCellRenderer',
          isHaircutOrHaircutTypeField: true
        },
        editable: canEditNegotiationDetailHaircut,
        cellEditorParams: {
          inputComponent: NumericInput,
          inputParams: (negotiationDetail: NegotiationDetail, context: TypedContext<NegotiationDetail>) => ({
            formatOptions: getHaircutFormatOptions(
              undefined,
              context.userPreference.haircutSign,
              negotiationDetail.ownOrder,
              negotiationDetail.direction
            ),
            updateOnBlurOnly: negotiationDetail.negotiation.negotiationType === NegotiationType.CONTINGENT_SUMMARY,
            onPaste: stopPastePropagation,
            dataTestId: 'LiveNegotiationsGridModel-HaircutNumericInput'
          }),
          rowDataGetter: negotiationRowDataGetter,
          getNodeId: (negotiationDetail: NegotiationDetail) => negotiationDetail.negotiation.negotiationId,
          defaultField: NegotiationFieldNames.HAIRCUT_NEGOTIATED,
          updateField: NegotiationFieldNames.HAIRCUT_MODIFIED,
          isModifiedField: NegotiationFieldNames.HAIRCUT_IS_MODIFIED,
          validationField: NegotiationFieldNames.HAIRCUT
        },
        lockPinned: true,
        enableRowGroup: false
      },
      {
        colId: LiveGridColumnId.NEGOTIATION_HAIRCUT_TARGET,
        valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
          const rowData = negotiationRowDataGetter(params)
          return getTargetValueIfNotSummary(rowData, rowData?.negotiation.haircut.original)
        },
        headerName: Locale.translate(Translation.TC_TARGET),
        equals: () => false,
        width: 100,
        type: 'numericColumn',
        filter: 'agNumberColumnFilter',
        headerClass: 'group-header-cell group-header-cell-right-align',
        headerComponentParams: {
          parent: LiveGridColumnId.NEGOTIATION_HAIRCUT
        },
        valueFormatter: makeHaircutFormatter(),
        filterValueGetter: makeHaircutFilterGetter(),
        getQuickFilterText: makeHaircutQuickFilterTextGetter(),
        enableRowGroup: false,
        hide: true // delta groups are initially hidden
      },
      {
        colId: LiveGridColumnId.HAIRCUT_DELTA,
        valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
          const rowData = negotiationRowDataGetter(params)
          return getTargetValueIfNotOcoSummary(rowData, getHaircutDelta(rowData?.negotiation.haircut))
        },
        headerName: Locale.translate(Translation.TC_HAIRCUT),
        equals: () => false,
        width: 80,
        type: 'numericColumn',
        filter: 'agNumberColumnFilter',
        headerClass: 'group-header-cell group-header-cell-right-align',
        headerComponentParams: {
          template: headerTemplateDelta,
          parent: LiveGridColumnId.NEGOTIATION_HAIRCUT,
          delta: true
        },
        valueFormatter: makeHaircutFormatter(true),
        filterValueGetter: makeHaircutFilterGetter(true),
        getQuickFilterText: makeHaircutQuickFilterTextGetter(true),
        enableRowGroup: false,
        hide: true // delta groups are initially hidden
      }
    ]
  },
  {
    marryChildren: true,
    children: [
      {
        colId: LiveGridColumnId.NEGOTIATION_START_CONSIDERATION,
        valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
          const rowData = negotiationRowDataGetter(params)
          return rowData && getTargetValueIfNotOcoSummary(rowData, getNegotiatedValue(rowData.negotiation.startConsideration))
        },
        headerName: Locale.translate(Translation.TC_START_CONSIDERATION),
        equals: () => false,
        width: 110,
        type: 'numericColumn',
        filter: 'agNumberColumnFilter',
        headerClass: 'group-header-cell',
        headerComponent: DeltaGroupHeaderComponent,
        headerComponentParams: {
          children: [
            LiveGridColumnId.NEGOTIATION_START_CONSIDERATION_ORIGINAL,
            LiveGridColumnId.START_CONSIDERATION_DELTA,
            LiveGridColumnId.NEGOTIATION_START_CONSIDERATION_OVERRIDE
          ]
        },
        valueFormatter: makeStartConsiderationFormatter(false, negotiationRowDataGetter),
        filterValueGetter: makeStartConsiderationFilterGetter(),
        cellRenderer: NegotiableCellRenderer,
        cellRendererParams: {
          getModificationType: swallowPrecisionError(({ negotiation }: NegotiationDetail) =>
            getStartConsiderationModificationType(negotiation.startConsideration)
          ),
          validationField: NegotiationFieldNames.START_CONSIDERATION,
          rowDataGetter: negotiationRowDataGetter
        },
        editable: canEditNegotiationDetailStartConsideration,
        cellEditorParams: {
          inputComponent: NumericInput,
          inputParams: (negotiationDetail: NegotiationDetail) => ({
            min: negotiationDetail.collateral?.ticketIncrement,
            step: negotiationDetail.collateral?.ticketIncrement,
            formatOptions: getStartConsiderationFormatOptions(),
            onPaste: stopPastePropagation
          }),
          defaultField: NegotiationFieldNames.START_CONSIDERATION_NEGOTIATED,
          updateField: NegotiationFieldNames.START_CONSIDERATION_MODIFIED,
          isModifiedField: NegotiationFieldNames.START_CONSIDERATION_IS_MODIFIED,
          validationField: NegotiationFieldNames.START_CONSIDERATION
        },
        lockPinned: true,
        enableRowGroup: false
      },
      {
        colId: LiveGridColumnId.NEGOTIATION_START_CONSIDERATION_OVERRIDE,
        headerName: Locale.translate(Translation.TC_START_CONSIDERATION_OVERRIDE),
        valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
          const rowData = negotiationRowDataGetter(params)

          if (rowData && hasNegotiationNestedLegs(rowData)) {
            return null
          }

          return rowData && getTargetValueIfNotOcoSummary(rowData, getNegotiatedValue(rowData.negotiation.startConsiderationOverride))
        },
        valueFormatter: booleanFormatter,
        equals: () => false,
        width: 120,
        filter: 'agSetColumnFilter',
        headerClass: 'group-header-cell group-header-cell-right-align',
        headerComponentParams: {
          parent: LiveGridColumnId.NEGOTIATION_START_CONSIDERATION
        },
        cellRenderer: NegotiableCellRenderer,
        cellRendererParams: {
          getModificationType: swallowPrecisionError(({ negotiation }: NegotiationDetail) =>
            getStartConsiderationOverrideModificationType(negotiation.startConsiderationOverride)
          ),
          validationField: NegotiationFieldNames.START_CONSIDERATION_OVERRIDE,
          rowDataGetter: negotiationRowDataGetter
        },
        cellEditorParams: {
          inputComponent: BooleanSelectbox,
          inputParams: () => ({
            withPortal: true
          }),
          defaultField: NegotiationFieldNames.START_CONSIDERATION_OVERRIDE_NEGOTIATED,
          updateField: NegotiationFieldNames.START_CONSIDERATION_OVERRIDE_MODIFIED,
          isModifiedField: NegotiationFieldNames.START_CONSIDERATION_OVERRIDE_IS_MODIFIED,
          validationField: NegotiationFieldNames.START_CONSIDERATION_OVERRIDE
        },
        editable: canEditIncomingNegotiationDetailStartConsiderationOverride,
        lockPinned: true,
        enableRowGroup: false
      },
      {
        colId: LiveGridColumnId.NEGOTIATION_START_CONSIDERATION_ORIGINAL,
        valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
          const rowData = negotiationRowDataGetter(params)
          return getTargetValueIfNotSummary(rowData, rowData?.negotiation.startConsideration.original)
        },
        headerName: Locale.translate(Translation.TC_TARGET),
        width: 115,
        type: 'numericColumn',
        filter: 'agNumberColumnFilter',
        headerClass: 'group-header-cell group-header-cell-right-align',
        headerComponentParams: {
          parent: LiveGridColumnId.NEGOTIATION_START_CONSIDERATION
        },
        valueFormatter: makeStartConsiderationFormatter(false, negotiationRowDataGetter),
        filterValueGetter: makeStartConsiderationFilterGetter(),
        enableRowGroup: false,
        hide: true // delta groups are initially hidden
      },
      {
        colId: LiveGridColumnId.START_CONSIDERATION_DELTA,
        valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
          const rowData = negotiationRowDataGetter(params)
          return getTargetValueIfNotOcoSummary(rowData, getStartConsiderationDelta(rowData?.negotiation.startConsideration))
        },
        headerName: Locale.translate(Translation.TC_START_CONSIDERATION),
        width: 110,
        type: 'numericColumn',
        filter: 'agNumberColumnFilter',
        headerClass: 'group-header-cell group-header-cell-right-align',
        headerComponentParams: {
          template: headerTemplateDelta,
          parent: LiveGridColumnId.NEGOTIATION_START_CONSIDERATION,
          delta: true
        },
        valueFormatter: makeStartConsiderationFormatter(true, negotiationRowDataGetter),
        filterValueGetter: makeStartConsiderationFilterGetter(true),
        getQuickFilterText: makeNumberQuickFilterTextGetter({ sign: true }),
        enableRowGroup: false,
        hide: true // delta groups are initially hidden
      }
    ]
  },
  {
    colId: LiveGridColumnId.NEGOTIATION_BOND_PRICE,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
      const rowData = negotiationRowDataGetter(params)
      return rowData && getTargetValueIfNotOcoSummary(rowData, getNegotiatedValue(rowData.negotiation.bondPrice))
    },
    headerName: Locale.translate(Translation.TC_COLLATERAL_PRICE),
    equals: () => false,
    width: 120,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    valueFormatter: makeNumberFormatter({
      maxFractionDigits: constants.bondPriceMaxFractionDigits
    }),
    filterValueGetter: makeNumberFilterGetter(),
    cellRenderer: NegotiableCellRenderer,
    cellRendererParams: {
      getModificationType: ({ negotiation }: NegotiationDetail) => getBondPriceModificationType(negotiation.bondPrice),
      validationField: NegotiationFieldNames.BOND_PRICE,
      rowDataGetter: negotiationRowDataGetter
    },
    editable: canEditNegotiationDetailBondPrice,
    cellEditorParams: {
      inputComponent: NumericInput,
      inputParams: () => ({
        min: 0,
        maxLength: constants.bondPriceMaxFieldLength,
        formatOptions: {
          maxFractionDigits: constants.bondPriceMaxFractionDigits
        },
        onPaste: stopPastePropagation
      }),
      defaultField: NegotiationFieldNames.BOND_PRICE_NEGOTIATED,
      updateField: NegotiationFieldNames.BOND_PRICE_MODIFIED,
      isModifiedField: NegotiationFieldNames.BOND_PRICE_IS_MODIFIED,
      validationField: NegotiationFieldNames.BOND_PRICE
    },
    lockPinned: true,
    enableRowGroup: false
  },
  {
    colId: LiveGridColumnId.NEGOTIATION_ALL_IN_PRICE,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
      const rowData = negotiationRowDataGetter(params)
      return rowData && getTargetValueIfNotSummary(rowData, rowData.negotiation.allInPrice)
    },
    headerName: Locale.translate(Translation.TC_ALL_IN_PRICE),
    width: 120,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    valueFormatter: makeNumberFormatter(),
    filterValueGetter: makeNumberFilterGetter(),
    enableRowGroup: false
  },
  {
    ...BASE_REPO_TYPE,
    colId: LiveGridColumnId.NEGOTIATION_REPO_TYPE,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.negotiation.repoType
  },
  {
    colId: LiveGridColumnId.NEGOTIATION_END_CONSIDERATION,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
      const rowData = negotiationRowDataGetter(params)
      return getTargetValueIfNotOcoSummary(rowData, rowData?.negotiation.endConsideration)
    },
    headerName: Locale.translate(Translation.TC_END_CONSIDERATION),
    width: 150,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    valueFormatter: makeNumberFormatter({ minDp: 2 }),
    enableRowGroup: false
  },
  {
    colId: LiveGridColumnId.COLLATERAL_ISIN_OR_BUI,
    valueGetter: makeIsinGetter(negotiationRowDataGetter),
    headerName: Locale.translate(Translation.TC_ISIN_BUI),
    width: 140,
    filter: 'agSetColumnFilter'
  },
  {
    colId: LiveGridColumnId.FUND_NAME,
    headerName: Locale.translate(Translation.TC_MY_ENTITY),
    valueGetter: makeTradingEntityNameGetter(true, negotiationRowDataGetter),
    equals: () => false,
    cellRenderer: NegotiableCellRenderer,
    cellRendererParams: {
      getModificationType: ({ negotiation }: NegotiationDetail) => getStringModificationType(negotiation.counterpartyIdNegotiableField),
      validationField: NegotiationFieldNames.COUNTERPARTY_NAME,
      rowDataGetter: negotiationRowDataGetter
    },
    cellEditorParams: (params: { node: TypedRowNode<NegotiationDetail> }) => ({
      inputComponent: CounterpartyMultiLegCombobox,
      inputParams: (negotiationDetail: NegotiationDetail): Partial<CounterpartyMultiLegComboboxProps> => {
        const { negotiation } = negotiationDetail
        const isSummary = isSummaryNegotiationType(negotiation.negotiationType)

        const details =
          isSummary && params.node.group && params.node.allLeafChildren
            ? params.node.allLeafChildren.map(c => c.data).filter(isNotNil)
            : [negotiationDetail]

        const filters = details.map<CounterpartyFilter>(({ negotiation: n }) => ({
          isin: n.collateralId,
          bui: n.basketAllocation?.basketId ?? null,
          secondaryBasketId: n.secondaryBasketId && getNegotiatedValue(n.secondaryBasketId),
          secondaryCollateralId: n.secondaryCollateralId && getNegotiatedValue(n.secondaryCollateralId),
          fundId: n.fundId,
          repoType: n.repoType,
          masterLegalAgreementType: n.masterLegalAgreementType,
          marketType: n.marketType
        }))

        return {
          negotiationDetail,
          filters,
          settlementVenue: getNegotiatedValue(negotiation.settlementVenueNegotiable),
          dataTestId: 'IncomingNegotiationsGridModel-CounterpartyCombobox',
          onPaste: stopPastePropagation,
          withPortal: true
        }
      },
      rowDataGetter: negotiationRowDataGetter,
      getNodeId: (negotiationDetail: NegotiationDetail) => negotiationDetail.negotiation.negotiationId,
      defaultField: NegotiationFieldNames.COUNTERPARTY_NAME_NEGOTIATED,
      updateField: NegotiationFieldNames.COUNTERPARTY_NAME_MODIFIED,
      isModifiedField: NegotiationFieldNames.COUNTERPARTY_NAME_IS_MODIFIED,
      validationField: NegotiationFieldNames.COUNTERPARTY_NAME
    }),
    editable: canEditNegotiationDetailCounterpartyId,
    width: 150,
    filter: 'agSetColumnFilter',
    enableRowGroup: false,
    lockPinned: true
  },
  {
    colId: LiveGridColumnId.NEGOTIATION_AXE_ROLL_UNWIND,
    headerName: Locale.translate(Translation.TC_AXE_ROLL_UNWIND),
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.axeTradeCondition,
    equals: () => false,
    cellRenderer: withAllContext(createAxeTradeConditionRenderer(LiveAxeTradeConditionButtons)),
    keyCreator: axeTradeConditionKeyCreator,
    getQuickFilterText: axeTradeConditionFilterTextGetter,
    valueFormatter: axeTradeConditionFilterTextGetter,
    width: 130,
    filter: 'agSetColumnFilter',
    filterParams: { valueFormatter: axeFilterValueFormatter },
    sortable: false
  },
  {
    colId: LiveGridColumnId.COLLATERAL_CURRENCY,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.negotiation.currency,
    headerName: Locale.translate(Translation.TC_CURRENCY),
    width: 90,
    filter: 'agSetColumnFilter'
  },
  {
    colId: LiveGridColumnId.INVESTMENT_DECISION_MAKER,
    headerName: Locale.translate(Translation.TC_INVESTMENT_DECISION_MAKER),
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
      const rowData = negotiationRowDataGetter(params)
      return rowData?.responderInvestmentDecisionMaker && DecisionMakerHelper.getName(rowData.responderInvestmentDecisionMaker)
    },
    equals: () => false,
    width: 180,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: NegotiationFieldNames.RESPONDER_INVESTMENT_DECISION_MAKER,
      rowDataGetter: negotiationRowDataGetter
    },
    cellEditorParams: {
      inputComponent: DecisionMakerSelectbox,
      inputParams: () => ({
        onPaste: stopPastePropagation,
        withPortal: true
      }),
      defaultField: NegotiationFieldNames.RESPONDER_INVESTMENT_DECISION_MAKER_NEGOTIATED,
      updateField: NegotiationFieldNames.RESPONDER_INVESTMENT_DECISION_MAKER_MODIFIED,
      isModifiedField: NegotiationFieldNames.RESPONDER_INVESTMENT_DECISION_MAKER_IS_MODIFIED,
      validationField: NegotiationFieldNames.RESPONDER_INVESTMENT_DECISION_MAKER
    },
    editable: canEditNegotiationDetailGeneral,
    lockPinned: true,
    enableRowGroup: false
  },
  {
    colId: LiveGridColumnId.EXECUTION_DECISION_MAKER,
    headerName: Locale.translate(Translation.TC_EXECUTION_DECISION_MAKER),
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
      const rowData = negotiationRowDataGetter(params)
      return rowData?.responderExecutionDecisionMaker && DecisionMakerHelper.getName(rowData.responderExecutionDecisionMaker)
    },
    equals: () => false,
    width: 180,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: NegotiationFieldNames.RESPONDER_EXECUTION_DECISION_MAKER,
      rowDataGetter: negotiationRowDataGetter
    },
    cellEditorParams: {
      inputComponent: DecisionMakerSelectbox,
      inputParams: () => ({
        onPaste: stopPastePropagation,
        withPortal: true
      }),
      defaultField: NegotiationFieldNames.RESPONDER_EXECUTION_DECISION_MAKER_NEGOTIATED,
      updateField: NegotiationFieldNames.RESPONDER_EXECUTION_DECISION_MAKER_MODIFIED,
      isModifiedField: NegotiationFieldNames.RESPONDER_EXECUTION_DECISION_MAKER_IS_MODIFIED,
      validationField: NegotiationFieldNames.RESPONDER_EXECUTION_DECISION_MAKER
    },
    editable: canEditNegotiationDetailGeneral,
    lockPinned: true,
    enableRowGroup: false
  },
  {
    colId: LiveGridColumnId.NEGOTIATION_RFQ_TYPE,
    headerName: Locale.translate(Translation.TC_RFQ_TYPE),
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
      const rowData = negotiationRowDataGetter(params)
      return rowData && getTargetValueIfNotOcoSummary(rowData, getNegotiatedValue(rowData.negotiation.rfqType))
    },
    valueFormatter: rfqTypeFormatter,
    width: 100,
    filter: 'agSetColumnFilter',
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      getModificationType: ({ negotiation }: NegotiationDetail) => getRFQTypeModificationType(negotiation.rfqType),
      validationField: NegotiationFieldNames.RFQ_TYPE,
      rowDataGetter: negotiationRowDataGetter
    },
    cellEditorParams: {
      inputComponent: RFQTypeSelectbox,
      inputParams: () => ({
        withPortal: true
      }),
      rowDataGetter: negotiationRowDataGetter,
      getNodeId: (negotiationDetail: NegotiationDetail) => negotiationDetail.negotiation.negotiationId,
      defaultField: NegotiationFieldNames.RFQ_TYPE_NEGOTIATED,
      updateField: NegotiationFieldNames.RFQ_TYPE_MODIFIED,
      isModifiedField: NegotiationFieldNames.RFQ_TYPE_IS_MODIFIED,
      validationField: NegotiationFieldNames.RFQ_TYPE,
      onPaste: stopPastePropagation
    },
    editable: canEditNegotiationDetailRFQType,
    lockPinned: true
  }
]

const HIDDEN_COLUMNS = [
  /* ====================================================== Hidden by default ====================================================== */
  {
    colId: StagingGridColumnId.ORDER_PREV_RATE,
    headerName: Locale.translate(Translation.TC_PREV_RATE),
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
      return negotiationRowDataGetter(params)?.negotiation.servicingTrigger?.previousRate
    },
    valueFormatter: makeRateFormatter(),
    filterValueGetter: makePrevRateFilterGetter(),
    width: 150,
    filter: 'agSetColumnFilter',
    hide: true
  },
  {
    colId: LiveGridColumnId.COUNTERPARTY_FIRM,
    valueGetter: makeOrganisationFieldGetter('legalName', false, negotiationRowDataGetter),
    headerName: Locale.translate(Translation.TC_COUNTERPARTY_FIRM),
    width: 150,
    filter: 'agSetColumnFilter'
  },
  {
    colId: ClearingGridColumnId.COUNTERPARTY_CLEARING,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
      const rowData = negotiationRowDataGetter(params)
      return rowData && getNegotiationClearingStatusForNonSummary(rowData)
    },
    headerName: Locale.translate(Translation.TC_COUNTERPARTY_CLEARED),
    width: 70,
    filter: 'agSetColumnFilter'
  },
  {
    colId: ClearingGridColumnId.COUNTERPARTY_SPONSOR,
    valueGetter: makeSponsorNameGetter(true, negotiationRowDataGetter),
    headerName: Locale.translate(Translation.TC_COUNTERPARTY_SPONSOR),
    width: 170,
    filter: 'agSetColumnFilter',
    editable: canEditNegotiationDetailCounterpartySponsor,
    cellEditorParams: {
      inputComponent: CounterpartySponsorCombobox,
      inputParams: (negotiationDetail: NegotiationDetail) => ({
        counterpartyId: getNegotiatedValue(negotiationDetail.negotiation.counterpartyIdNegotiableField),
        clearingHouseId: negotiationDetail.negotiation.clearingHouseId,
        productSectorId: negotiationDetail.productSector?.id,
        onPaste: stopPastePropagation
      }),
      defaultField: NegotiationFieldNames.COUNTERPARTY_SPONSOR_ID_NEGOTIATED,
      updateField: NegotiationFieldNames.COUNTERPARTY_SPONSOR_ID_MODIFIED,
      isModifiedField: NegotiationFieldNames.COUNTERPARTY_SPONSOR_ID_IS_MODIFIED,
      validationField: NegotiationFieldNames.COUNTERPARTY_SPONSOR_ID
    },
    lockPinned: true,
    enableRowGroup: false
  },
  {
    colId: ClearingGridColumnId.EXECUTING_COUNTERPARTY_NAME,
    valueGetter: makeExecutingTradingEntityNameGetter(negotiationRowDataGetter),
    headerName: Locale.translate(Translation.TC_EXECUTING_COUNTERPARTY),
    width: 170,
    filter: 'agSetColumnFilter'
  },
  {
    colId: LiveGridColumnId.NEGOTIATION_LAST_INITIATOR,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.negotiation.lastInitiatorId,
    headerName: Locale.translate(Translation.TC_LAST_INITIATOR),
    width: 150,
    filter: 'agSetColumnFilter'
  },
  {
    colId: LiveGridColumnId.NEGOTIATION_LAST_RESPONDER,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.negotiation.lastResponderId,
    headerName: Locale.translate(Translation.TC_LAST_RESPONDER),
    width: 150,
    filter: 'agSetColumnFilter'
  },
  {
    colId: LiveGridColumnId.NEGOTIATION_UPDATED_BY,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.negotiation.lastUpdatedBy,
    headerName: Locale.translate(Translation.TC_LAST_UPDATED_BY),
    width: 130,
    filter: 'agSetColumnFilter'
  },
  {
    colId: LiveGridColumnId.NEGOTIATION_UPDATED_AT_DATE,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.negotiation.lastUpdatedAt,
    getQuickFilterText: dateQuickFilterTextGetter,
    headerName: Locale.translate(Translation.TC_LAST_UPDATED_AT_DATE),
    width: 130,
    filter: 'agDateColumnFilter',
    ...dateFilterParams,
    valueFormatter: makeDateFormatter(),
    enableRowGroup: false
  },
  {
    colId: LiveGridColumnId.NEGOTIATION_UPDATED_AT_TIME,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.negotiation.lastUpdatedAt,
    headerName: Locale.translate(Translation.TC_LAST_UPDATED_AT_TIME),
    width: 130,
    filter: false,
    valueFormatter: makeTimeFormatter(),
    getQuickFilterText: timeQuickFilterTextGetter,
    enableRowGroup: false
  },
  {
    colId: CollateralBasketColumnId.COLLATERAL_BASKET_DESCRIPTION,
    headerName: Locale.translate(Translation.TC_BASKET_DESCRIPTION),
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.collateralBasket?.description,
    width: 220,
    filter: 'agSetColumnFilter'
  },
  {
    colId: CollateralBasketColumnId.COLLATERAL_BASKET_DBV_CLASS,
    headerName: Locale.translate(Translation.TC_DBV_CLASS),
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.collateralBasket?.dbvClass,
    width: 120,
    filter: 'agSetColumnFilter'
  },
  {
    colId: LiveGridColumnId.EVERGREEN_NOTICE_PERIOD,
    headerName: Locale.translate(Translation.TC_EVERGREEN_NOTICE_PERIOD),
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
      const rowData = negotiationRowDataGetter(params)
      return rowData?.negotiation?.evergreenNoticePeriod
    },
    valueFormatter: evergreenNoticeFormatter,
    getQuickFilterText: evergreenNoticePeriodQuickfixFilterTextGetter,
    width: 120,
    filter: 'agSetColumnFilter',
    enableRowGroup: false
  },
  {
    colId: LiveGridColumnId.NEGOTIATION_CALLABLE,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.negotiation.callableType,
    headerName: Locale.translate(Translation.TC_CALLABLE),
    width: 100,
    filter: 'agSetColumnFilter',
    valueFormatter: callableTypeFormatter
  },
  {
    colId: LiveGridColumnId.NEGOTIATION_CALL_DAYS,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.negotiation.callablePeriodDays,
    headerName: Locale.translate(Translation.TC_CALL_DAYS),
    width: 100,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    valueFormatter: makeNumberFormatter(),
    enableRowGroup: false
  },
  {
    colId: LiveGridColumnId.NEGOTIATION_ACCRUED_INTEREST,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
      const rowData = negotiationRowDataGetter(params)
      return getTargetValueIfNotOcoSummary(rowData, rowData?.negotiation.accruedInterest)
    },
    headerName: Locale.translate(Translation.TC_COLLATERAL_ACCRUED_INTEREST),
    width: 180,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    valueFormatter: makeNumberFormatterSummaryRow({ minDp: 2 }, negotiationRowDataGetter),
    enableRowGroup: false
  },
  {
    colId: LiveGridColumnId.NEGOTIATION_COMPETITION_COUNT,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
      const rowData = negotiationRowDataGetter(params)
      return params.node.group ? rowData?.negotiation.competitionCount ?? '' : rowData?.negotiation.competitionCount
    },
    headerName: Locale.translate(Translation.TC_COMPETITION),
    width: 120,
    filter: 'agNumberColumnFilter',
    enableRowGroup: false
  },
  {
    colId: LiveGridColumnId.REFERENCE_RATE_NAME,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.referenceRate?.name,
    headerName: Locale.translate(Translation.TC_REFERENCE_RATE),
    width: 120,
    filter: 'agSetColumnFilter'
  },
  {
    colId: LiveGridColumnId.FLOAT_CRYSTALLISATION_DAYS,
    headerName: Locale.translate(Translation.TC_FLOAT_CRYSTALLISATION_DAYS),
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.floatCrystallisationDays,
    width: 120,
    filter: 'agSetColumnFilter',
    equals: () => false,
    editable: false,
    lockPinned: true
  },
  {
    colId: LiveGridColumnId.NEGOTIATION_SETTLEMENT_VENUE,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
      const rowData = negotiationRowDataGetter(params)
      return rowData && getSettlementVenueName(getNegotiatedValue(rowData.negotiation.settlementVenueNegotiable))
    },
    headerName: Locale.translate(Translation.TC_SETTLEMENT),
    equals: () => false,
    width: 150,
    filter: 'agSetColumnFilter',
    cellRenderer: NegotiableCellRenderer,
    cellRendererParams: {
      getModificationType: ({ negotiation }: NegotiationDetail) => getStringModificationType(negotiation.settlementVenueNegotiable),
      validationField: NegotiationFieldNames.SETTLEMENT_VENUE,
      rowDataGetter: negotiationRowDataGetter
    },
    cellEditorParams: {
      inputComponent: SettlementVenueIncomingSettlementSelectbox,
      inputParams: (negotiationDetail: NegotiationDetail) => ({
        negotiationDetail,
        onPaste: stopPastePropagation,
        withPortal: true
      }),
      rowDataGetter: negotiationRowDataGetter,
      getNodeId: (negotiationDetail: NegotiationDetail) => negotiationDetail.negotiation.negotiationId,
      defaultField: NegotiationFieldNames.SETTLEMENT_VENUE_NEGOTIATED,
      updateField: NegotiationFieldNames.SETTLEMENT_VENUE_MODIFIED,
      isModifiedField: NegotiationFieldNames.SETTLEMENT_VENUE_IS_MODIFIED,
      validationField: NegotiationFieldNames.SETTLEMENT_VENUE
    },
    editable: canEditNegotiationDetailRowSettlementType,
    lockPinned: true,
    enableRowGroup: true
  },
  {
    colId: LiveGridColumnId.PRODUCT,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.product?.name,
    headerName: Locale.translate(Translation.TC_PRODUCT),
    width: 80,
    filter: 'agSetColumnFilter'
  },
  {
    colId: LiveGridColumnId.NEGOTIATION_CREATED_AT_DATE,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.negotiation.createdAt,
    headerName: Locale.translate(Translation.TC_CREATION_DATE),
    width: 110,
    filter: 'agDateColumnFilter',
    ...dateFilterParams,
    valueFormatter: makeDateFormatter(),
    getQuickFilterText: dateQuickFilterTextGetter,
    enableRowGroup: false
  },
  {
    colId: LiveGridColumnId.NEGOTIATION_CREATED_AT_TIME,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.negotiation.createdAt,
    headerName: Locale.translate(Translation.TC_CREATION_TIME),
    width: 110,
    filter: false,
    valueFormatter: makeTimeFormatter(),
    getQuickFilterText: timeQuickFilterTextGetter,
    enableRowGroup: false
  },
  {
    colId: LiveGridColumnId.NEGOTIATION_CSD,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.negotiation.csd,
    headerName: Locale.translate(Translation.TC_CSD),
    width: 100,
    filter: 'agSetColumnFilter'
  },
  {
    colId: LiveGridColumnId.NEGOTIATION_FACTOR,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.negotiation.factor,
    headerName: Locale.translate(Translation.TC_FACTOR),
    width: 100,
    filter: 'agSetColumnFilter'
  },
  {
    ...BASE_ASSET_TYPE,
    colId: LiveGridColumnId.NEGOTIATION_ASSET_TYPE,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.negotiation.assetType,
    width: 100
  },
  {
    colId: LiveGridColumnId.NEGOTIATION_ABSOLUTE_CONSIDERATION,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.negotiation.absoluteConsideration,
    headerName: Locale.translate(Translation.TC_ABSOLUTE_CONSIDERATION),
    width: 70,
    minWidth: 70,
    headerClass: 'group-header-cell group-header-cell-right-align',
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    valueFormatter: makeNumberFormatter({ minDp: 2 }),
    enableRowGroup: false
  },
  {
    colId: LiveGridColumnId.NEGOTIATION_TRADE_TYPE,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
      const rowData = negotiationRowDataGetter(params)
      return getTargetValueIfNotOcoSummary(rowData, rowData?.negotiation.repoClassification)
    },
    headerName: Locale.translate(Translation.TC_TRADE_TYPE),
    width: 100,
    filter: 'agSetColumnFilter',
    valueFormatter: repoClassificationFormatter
  },
  {
    colId: ClearingGridColumnId.CLEARING_VENUE,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
      const rowData = negotiationRowDataGetter(params)
      return rowData?.clearingHouse?.publishedName
    },
    headerName: Locale.translate(Translation.TC_CLEARING_VENUE),
    width: 150,
    filter: 'agSetColumnFilter'
  },
  {
    colId: LiveGridColumnId.COUNTERPARTY_LEI,
    valueGetter: makeTradingEntityLeiGetter(false, negotiationRowDataGetter),
    headerName: Locale.translate(Translation.TC_COUNTERPARTY_LEI),
    width: 150,
    filter: 'agSetColumnFilter'
  },
  {
    colId: LiveGridColumnId.COUNTERPARTY_BIC,
    valueGetter: makeTradingEntityFieldGetter('bic', false, negotiationRowDataGetter),
    headerName: Locale.translate(Translation.TC_COUNTERPARTY_BIC),
    width: 150,
    filter: 'agSetColumnFilter'
  },
  {
    colId: LiveGridColumnId.COUNTERPARTY_FIRM_LEI,
    valueGetter: makeOrganisationFieldGetter('lei', false, negotiationRowDataGetter),
    headerName: Locale.translate(Translation.TC_COUNTERPARTY_FIRM_LEI),
    width: 150,
    filter: 'agSetColumnFilter'
  },
  {
    colId: LiveGridColumnId.COLLATERAL_COUPON,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.collateral?.coupon,
    headerName: Locale.translate(Translation.TC_COUPON),
    width: 80,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    valueFormatter: makeNumberFormatter({ percent: true, minDp: 1, decimalShift: 2 }),
    filterValueGetter: makeNumberFilterGetter({ percent: true, minDp: 1, decimalShift: 2 }),
    getQuickFilterText: makeNumberQuickFilterTextGetter({ percent: true }),
    enableRowGroup: false
  },
  {
    colId: LiveGridColumnId.NEGOTIATION_REPO_INTEREST,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
      const rowData = negotiationRowDataGetter(params)
      return getTargetValueIfNotOcoSummary(rowData, rowData?.negotiation.repoInterest)
    },
    headerName: Locale.translate(Translation.TC_REPO_INTEREST),
    width: 100,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    valueFormatter: makeNumberFormatterSummaryRow({ minDp: 2 }, negotiationRowDataGetter, true),
    enableRowGroup: false,
    cellClassRules: {
      'paren-on-negative-cell': (params: any) => params.node.group && params.value > 0
    }
  },
  {
    colId: LiveGridColumnId.COLLATERAL_ISSUER,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.collateral?.issuer,
    headerName: Locale.translate(Translation.TC_ISSUER),
    width: 80,
    filter: 'agSetColumnFilter'
  },
  {
    colId: LiveGridColumnId.BLOCK_TRADE,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
      const rowData = negotiationRowDataGetter(params)
      return getTargetValueIfNotSummary(rowData, rowData?.negotiation.blockTrade) ?? undefined
    },
    headerName: Locale.translate(Translation.TC_BLOCK),
    width: 80,
    filter: 'agSetColumnFilter',
    valueFormatter: booleanFormatter
  },
  {
    colId: LiveGridColumnId.FUND_LEI,
    valueGetter: makeTradingEntityLeiGetter(true, negotiationRowDataGetter),
    headerName: Locale.translate(Translation.TC_MY_ENTITY_LEI),
    width: 170,
    filter: 'agSetColumnFilter'
  },
  {
    colId: LiveGridColumnId.FUND_BIC,
    valueGetter: makeTradingEntityFieldGetter('bic', true, negotiationRowDataGetter),
    headerName: Locale.translate(Translation.TC_MY_ENTITY_BIC),
    width: 170,
    filter: 'agSetColumnFilter'
  },
  {
    colId: LiveGridColumnId.FUND_FIRM,
    valueGetter: makeOrganisationFieldGetter('legalName', true, negotiationRowDataGetter),
    headerName: Locale.translate(Translation.TC_MY_FIRM),
    width: 150,
    filter: 'agSetColumnFilter'
  },
  {
    colId: LiveGridColumnId.FUND_FIRM_LEI,
    valueGetter: makeOrganisationFieldGetter('lei', true, negotiationRowDataGetter),
    headerName: Locale.translate(Translation.TC_MY_FIRM_LEI),
    width: 170,
    filter: 'agSetColumnFilter'
  },
  {
    colId: LiveGridColumnId.COLLATERAL_CUSIP,
    valueGetter: makeCusipGetter(negotiationRowDataGetter),
    headerName: Locale.translate(Translation.TC_CUSIP),
    width: 120,
    filter: 'agSetColumnFilter'
  },
  {
    colId: LiveGridColumnId.PRODUCT_SECTOR,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.productSector?.name,
    headerName: Locale.translate(Translation.TC_SECTOR),
    width: 100,
    filter: 'agSetColumnFilter'
  },
  {
    colId: LiveGridColumnId.NEGOTIATION_MARKET,
    headerName: Locale.translate(Translation.TC_MARKET),
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
      const market = negotiationRowDataGetter(params)?.negotiation.market
      return market && MarketHelper.getName(market)
    },
    width: 80,
    filter: 'agSetColumnFilter'
  },
  {
    colId: LiveGridColumnId.NEGOTIATION_ORDER_ID,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
      const rowData = negotiationRowDataGetter(params)
      return getTargetValueIfNotSummary(rowData, rowData?.negotiation.parentOrderId)
    },
    headerName: Locale.translate(Translation.TC_RFQ_ID),
    width: 100,
    filter: 'agSetColumnFilter'
  },
  {
    colId: LiveGridColumnId.NEGOTIATION_AXE_ID,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.negotiation.axeId,
    headerName: Locale.translate(Translation.TC_AXE_ID),
    width: 100,
    filter: 'agSetColumnFilter',
    enableRowGroup: false
  },
  {
    colId: CollectionColumnId.COLLECTION_ID,
    headerName: Locale.translate(Translation.TC_COLLECTION_ID),
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.collection?.id,
    filter: 'agSetColumnFilter',
    width: 150
  },
  {
    colId: LiveGridColumnId.NEGOTIATION_NOTICE_PERIOD_DAYS,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.negotiation.noticePeriodDays,
    headerName: Locale.translate(Translation.TC_NOTICE_PERIOD_DAYS),
    width: 80,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    valueFormatter: makeNumberFormatter(),
    enableRowGroup: false
  },
  {
    colId: LiveGridColumnId.PRIME_BROKER_NAME,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) =>
      negotiationRowDataGetter(params)?.negotiation.initiatorPrimeBrokerName,
    headerName: Locale.translate(Translation.TC_PRIME_BROKER),
    width: 180,
    filter: 'agSetColumnFilter'
  },
  {
    colId: LiveGridColumnId.NEGOTIATION_PRE_AGREED,
    headerName: Locale.translate(Translation.TC_PRE_AGREED),
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
      const rowData = negotiationRowDataGetter(params)
      return rowData && getTargetValueIfNotOcoSummary(rowData, getNegotiatedValue(rowData.negotiation.preAgreed))
    },
    valueFormatter: booleanFormatter,
    equals: () => false,
    width: 120,
    filter: 'agSetColumnFilter',
    cellRenderer: NegotiableCellRenderer,
    cellRendererParams: {
      getModificationType: ({ negotiation }: NegotiationDetail) => getBooleanModificationType(negotiation.preAgreed),
      validationField: NegotiationFieldNames.PRE_AGREED,
      rowDataGetter: negotiationRowDataGetter
    },
    cellEditorParams: {
      inputComponent: BooleanSelectbox,
      inputParams: () => ({
        onPaste: stopPastePropagation,
        withPortal: true
      }),
      defaultField: NegotiationFieldNames.PRE_AGREED_NEGOTIATED,
      updateField: NegotiationFieldNames.PRE_AGREED_MODIFIED,
      isModifiedField: NegotiationFieldNames.PRE_AGREED_IS_MODIFIED,
      validationField: NegotiationFieldNames.PRE_AGREED
    },
    editable: canEditNegotiationPreAgreed,
    lockPinned: true,
    enableRowGroup: false
  },
  {
    colId: LiveGridColumnId.NEGOTIATION_MASTER_LEGAL_AGREEMENT,
    headerName: Locale.translate(Translation.TC_MASTER_LEGAL_AGREEMENT),
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
      const rowData = negotiationRowDataGetter(params)
      return rowData && getTargetValueIfNotSummary(rowData, rowData.negotiation.masterLegalAgreementType)
    },
    width: 180,
    filter: 'agSetColumnFilter'
  },
  {
    colId: LiveGridColumnId.NEGOTIATION_PRIMARY_SALES_COVER,
    headerName: Locale.translate(Translation.TC_PRIMARY_SALES_COVER),
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
      const user = negotiationRowDataGetter(params)?.counterpartyPrimarySalesCoverUser
      return user ? UserHelper.getName(user) : null
    },
    width: 180,
    filter: 'agSetColumnFilter'
  },
  {
    colId: LiveGridColumnId.NEGOTIATION_SECONDARY_SALES_COVER,
    headerName: Locale.translate(Translation.TC_SECONDARY_SALES_COVER),
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
      const user = negotiationRowDataGetter(params)?.counterpartySecondarySalesCoverUser
      return user ? UserHelper.getName(user) : null
    },
    width: 180,
    filter: 'agSetColumnFilter'
  },
  {
    colId: LiveGridColumnId.NEGOTIATION_RESPONDER_BOOK,
    headerName: Locale.translate(Translation.TC_BOOK),
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
      const rowData = negotiationRowDataGetter(params)
      return rowData && getTargetValueIfNotOcoSummary(rowData, getNegotiatedValue(rowData.negotiation.responderBook))
    },
    equals: () => false,
    width: 180,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: NegotiationFieldNames.RESPONDER_BOOK,
      rowDataGetter: negotiationRowDataGetter
    },
    cellEditorParams: {
      inputComponent: TextInput,
      inputParams: () => ({
        onPaste: stopPastePropagation,
        maxLength: constants.internalBookingMaxFieldLength
      }),
      defaultField: NegotiationFieldNames.RESPONDER_BOOK_NEGOTIATED,
      updateField: NegotiationFieldNames.RESPONDER_BOOK_MODIFIED,
      isModifiedField: NegotiationFieldNames.RESPONDER_BOOK_IS_MODIFIED,
      validationField: NegotiationFieldNames.RESPONDER_BOOK
    },
    editable: canEditNegotiationDetailGeneral,
    lockPinned: true,
    enableRowGroup: false
  },
  {
    colId: LiveGridColumnId.NEGOTIATION_RESPONDER_STRATEGY,
    headerName: Locale.translate(Translation.TC_STRATEGY),
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
      const rowData = negotiationRowDataGetter(params)
      return rowData && getTargetValueIfNotOcoSummary(rowData, getNegotiatedValue(rowData.negotiation.responderStrategy))
    },
    equals: () => false,
    width: 180,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: NegotiationFieldNames.RESPONDER_STRATEGY,
      rowDataGetter: negotiationRowDataGetter
    },
    cellEditorParams: {
      inputComponent: TextInput,
      inputParams: () => ({
        onPaste: stopPastePropagation,
        maxLength: constants.internalBookingMaxFieldLength
      }),
      defaultField: NegotiationFieldNames.RESPONDER_STRATEGY_NEGOTIATED,
      updateField: NegotiationFieldNames.RESPONDER_STRATEGY_MODIFIED,
      isModifiedField: NegotiationFieldNames.RESPONDER_STRATEGY_IS_MODIFIED,
      validationField: NegotiationFieldNames.RESPONDER_STRATEGY
    },
    editable: canEditNegotiationDetailGeneral,
    lockPinned: true,
    enableRowGroup: false
  },
  {
    colId: LiveGridColumnId.NEGOTIATION_IS_CURVE,
    headerName: Locale.translate(Translation.TC_CURVE),
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => negotiationRowDataGetter(params)?.negotiation.isCurve,
    valueFormatter: booleanFormatter,
    width: 70,
    filter: 'agSetColumnFilter'
  },
  {
    colId: LiveGridColumnId.LINKED_TRADE_ID,
    headerName: Locale.translate(Translation.TC_LINKED_TRADE_ID),
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
      const rowData = negotiationRowDataGetter<NegotiationDetail>(params)
      return isNotNil(rowData) ? getTargetValueIfNotSummary(rowData, rowData.linkedTradeId) : null
    },
    width: 70,
    filter: 'agSetColumnFilter'
  },
  {
    colId: LiveGridColumnId.MY_ENTITY_SHORT_NAME,
    headerName: Locale.translate(Translation.TC_MY_ENTITY_SHORT_NAME),
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) =>
      negotiationRowDataGetter<NegotiationDetail>(params)?.counterparty?.shortName,
    width: 150,
    filter: 'agSetColumnFilter'
  },
  {
    colId: LiveGridColumnId.MY_FICC_ENTITY_SHORT_NAME,
    headerName: Locale.translate(Translation.TC_MY_FICC_ENTITY_SHORT_NAME),
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) =>
      negotiationRowDataGetter<NegotiationDetail>(params)?.counterparty?.ficcShortName,
    width: 150,
    filter: 'agSetColumnFilter',
    hide: true
  },
  {
    colId: LiveGridColumnId.BATCH_ID,
    headerName: Locale.translate(Translation.TC_BATCH_ID),
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) =>
      negotiationRowDataGetter<NegotiationDetail>(params)?.negotiation.batchId,
    width: 150,
    filter: 'agSetColumnFilter'
  },
  {
    ...BASE_ISSUE,
    colId: LiveGridColumnId.ISSUE,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) =>
      negotiationRowDataGetter<NegotiationDetail>(params)?.negotiation.collateralIssue
  },
  {
    ...BASE_ROE,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
      const rowData = negotiationRowDataGetter(params)
      return rowData && getTargetValueIfNotOcoSummary(rowData, negotiationRowDataGetter(params)?.negotiation.rightOfExit)
    },
    editable: false
  },
  {
    ...BASE_ROS,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
      const rowData = negotiationRowDataGetter(params)
      return rowData && getTargetValueIfNotOcoSummary(rowData, negotiationRowDataGetter(params)?.negotiation.rightOfSubstitution)
    },
    editable: false
  },
  {
    ...BASE_MARKET_TYPE,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
      const rowData = negotiationRowDataGetter(params)
      return rowData && getTargetValueIfNotOcoSummary(rowData, negotiationRowDataGetter(params)?.negotiation.marketType)
    }
  },
  {
    ...BASE_MIC,
    valueGetter: (params: TypedValueGetterParams<NegotiationDetail>) => {
      const rowData = negotiationRowDataGetter(params)
      return rowData && getTargetValueIfNotOcoSummary(rowData, negotiationRowDataGetter(params)?.negotiation?.marketIdentifierCode)
    }
  }
]
  .map(colDef => ({
    ...colDef,
    hide: true
  }))
  .sort((a, b) => a.headerName.localeCompare(b.headerName))

export const incomingNegotiationsGridColumnDefs: (ColGroupDef | ColDef)[] = VISIBLE_COLUMNS.concat(HIDDEN_COLUMNS)

export const incomingNegotiationsGridOptions: GridOptions = {
  ...gridOptionsBase,
  getRowId: ({ data }) => data.negotiation.negotiationId,
  masterDetail: true,
  getRowHeight: getDynamicDetailRowHeight((data: NegotiationDetail) => data?.negotiation?.nestedLegs?.legs?.length),
  isRowMaster: hasNegotiationNestedLegs,
  detailCellRenderer: IncomingPreAssignmentsSubGrid
}
